import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  TextField,
  TextFieldProps
} from '@mui/material'
import { SyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Option {
  label: string
  value: number
}

export interface GeneratorNominalCurrentAutocompleteProps extends Omit<
  TextFieldProps,
  'children' | 'value' | 'onChange'
> {
  value?: number | null
  onChange?: (event: SyntheticEvent, value: number | null) => void
}

export default function GeneratorNominalCurrentAutocomplete({
  value,
  label,
  onChange,
  ...props
}: GeneratorNominalCurrentAutocompleteProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('generator')

  /**
   * The options.
   */
  const options: Option[] = [
    { label: '2.5 A', value: 2.5 },
    { label: '3 A', value: 3 },
    { label: '4 A', value: 4 },
    { label: '5 A', value: 5 },
    { label: '6 A', value: 6 },
    { label: '8 A', value: 8 },
    { label: '10 A', value: 10 },
    { label: '12.5 A', value: 12.5 },
    { label: '13 A', value: 13 },
    { label: '15 A', value: 15 },
    { label: '16 A', value: 16 },
    { label: '20 A', value: 20 },
    { label: '25 A', value: 25 },
    { label: '32 A', value: 32 },
    { label: '40 A', value: 40 },
    { label: '50 A', value: 50 },
    { label: '63 A', value: 63 },
    { label: '80 A', value: 80 },
    { label: '100 A', value: 100 },
    { label: '125 A', value: 125 },
    { label: '160 A', value: 160 },
    { label: '200 A', value: 200 },
    { label: '250 A', value: 250 },
    { label: '315 A', value: 315 },
    { label: '355 A', value: 355 },
    { label: '400 A', value: 400 },
    { label: '500 A', value: 500 },
    { label: '630 A', value: 630 }
  ]

  const [inputValue, setInputValue] = useState<string | undefined>(
    value === undefined || value === null ? undefined : `${value}`
  )

  const isSomeOptionSelected = options.some((option) => option.value === value)

  const autocompleteValue: string | Option | null = isSomeOptionSelected ? `${value}` : null

  const handleChange = (
    event: SyntheticEvent,
    option: string | Option | null,
    reason: AutocompleteChangeReason
  ) => {
    if (typeof option === 'string') {
      if (option !== '' && !isNaN(parseFloat(option))) {
        onChange?.(event, parseFloat(option))

        if (isNumeric(option)) {
          setInputValue(option)
        }
      }
    } else if (option !== null) {
      onChange?.(event, option.value)
      setInputValue(option.value.toString())
    }
  }

  const handleInputChange = (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'clear' || value === '') {
      setInputValue('')
      onChange?.(event, null)
    } else if (isNumeric(value)) {
      setInputValue(value)
      onChange?.(event, parseFloat(value))
    }
  }

  function isNumeric(value: string): boolean {
    return /^(0|[1-9][0-9]*)([.][0-9]*)?$/.test(value)
  }

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      options={options}
      value={autocompleteValue}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField label={label ?? `${t('nominalCurrent')} (A)`} {...params} {...props} />
      )}
    />
  )
}
